import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { Config } from '@jwp/ott-common/types/config';
import type { Settings } from '@jwp/ott-common/types/settings';
import { getModule } from '@jwp/ott-common/src/modules/container';
import AppController from '@jwp/ott-common/src/controllers/AppController';
import type { AppError } from '@jwp/ott-common/src/utils/error';
import { logDebug, logError } from '@jwp/ott-common/src/logger';
import { CONFIG_QUERY_KEY } from '@jwp/ott-common/src/constants';
import useEventCallback from '@jwp/ott-hooks-react/src/useEventCallback';

type Resources = {
  config: Config;
  configSource: string | undefined;
  settings: Settings;
};

export type OnReadyCallback = (config: Config | undefined) => void;

const extractConfigId = (url: string) => {
  try {
    const parsedReferrerUrl = new URL(url);
    return parsedReferrerUrl.searchParams.get(CONFIG_QUERY_KEY);
  } catch {
    logDebug('useBootstrap', 'Failed to parse URL');
  }

  return null;
};

export const useBootstrapApp = (referrerUrl: string) => {
  const applicationController = getModule(AppController);
  const [isSwitchingLanguage, setIsSwitchingLanguage] = useState(false);
  const { i18n } = useTranslation();

  // extract the config key so we can re-bootstrap the app when this changes
  const configKey = extractConfigId(referrerUrl);

  const { data, isLoading, error, isSuccess, isFetched, refetch } = useQuery<Resources, Error | AppError>({
    queryKey: ['config-init', i18n.language, configKey],
    queryFn: () =>
      applicationController
        .initializeApp(referrerUrl, i18n.language)
        .catch((error) => {
          logError('useBootstrapApp', 'Error while bootstrapping the app', { error });
          throw error; // re-throw
        })
        .finally(() => {
          // when changing the `staleTime`, this finally statement is not enough to reset `isSwitchingLanguage`
          setIsSwitchingLanguage(false);
        }),
    refetchInterval: false,
    gcTime: 0,
    staleTime: 0,
    placeholderData: keepPreviousData,
    retry: 1,
  });

  const handleLanguageChanged = useEventCallback((newLanguage: string) => {
    if (newLanguage === i18n.language) return;
    setIsSwitchingLanguage(true);
  });

  // The app reloads when the language changes, but components/hook depending on the i18n.language will trigger before
  // the bootstrap query from above. This causes queries to update using old data and this is not updated anymore. Some
  // queries call APIs, which we don't want to do unnecessarily
  // By hooking into the `languageChanged` event and updating the state we immediately put the app in loading state and
  // prevent query race-conditions
  useEffect(() => {
    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n, handleLanguageChanged]);

  return {
    data,
    isLoading: isLoading || isSwitchingLanguage,
    error,
    isSuccess,
    isFetched,
    refetch,
  };
};

export type BootstrapData = ReturnType<typeof useBootstrapApp>;
